import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { FileEntry, MoveFileResult } from '@/lib/api/generated/model';
import { getMoveFailureCode, getMoveFailureMessage } from '../errors';
import { nameOf, parentOf, renamedPath } from '../folders';
import { fileKeys } from '../queryKeys';
import { useMoveWorkspaceFile } from './useMoveWorkspaceFile';

/**
 * Renames one file or folder where it stands.
 *
 * A rename is a move that holds the containing folder still, and the move
 * capability already does everything a rename needs: it fixes the set of
 * files up front, copies then deletes with rollback, records one `moved`
 * event per file so history follows, refuses a taken name, and caps the size
 * of a folder it will carry. So this composes `useMoveWorkspaceFile` rather
 * than restating any of it (research R3).
 *
 * What it changes is what the outcomes *mean* to the reader:
 *
 * - A taken name is a **name problem** to fix in the dialog, never an offer to
 *   overwrite. Move offers that for a file landing on a file; a rename that
 *   replaced a sibling would be a delete the reader did not ask for (FR-020).
 * - An item that is no longer there is reported **after** the listing has been
 *   refreshed, so the message and the page agree (FR-021).
 * - Everything else is worded the way move already words it — the ceiling
 *   message comes from the service, which knows the actual limit (FR-022).
 */
export function useRenameWorkspaceFile(
  workspaceId: string,
  {
    onRenamed,
    onNameTaken,
    onGone,
    onFailed,
  }: {
    onRenamed: (result: MoveFileResult) => void;
    /** The name the service refused; the dialog shows it inline. */
    onNameTaken: (name: string) => void;
    /** The item vanished under the reader; the listing is already refreshed. */
    onGone: (name: string) => void;
    /** Anything else, already worded for the reader. */
    onFailed: (message: string) => void;
  },
) {
  const queryClient = useQueryClient();

  const { move, pendingPaths } = useMoveWorkspaceFile(workspaceId, {
    onMoved: onRenamed,
    onConflict: (request) => {
      onNameTaken(nameOf(request.destination_path));
    },
    onFailed: (name, data, request) => {
      if (getMoveFailureCode(data) === 'FILE_NOT_FOUND') {
        void queryClient
          .invalidateQueries({
            queryKey: fileKeys.listing(
              workspaceId,
              parentOf(request.source_path),
            ),
          })
          .then(() => {
            onGone(name);
          });
        return;
      }
      onFailed(getMoveFailureMessage(name, data));
    },
  });

  const rename = useCallback(
    (entry: FileEntry, newName: string) => {
      move({
        source_path: entry.path,
        destination_path: renamedPath(entry.path, newName),
        // Never true from here: see the note on taken names above.
        overwrite: false,
      });
    },
    [move],
  );

  return { rename, pendingPaths };
}
